import { Flex } from '@adobe/react-spectrum';
import { error } from '@react/react-spectrum/Toast';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { featuredApi, handleApiCall } from '@exchange-frontends/api';
import { ErrorBoundary } from '@exchange-frontends/components';
import { useRootHref } from '@exchange-frontends/custom-hooks';

import { ContainerContext } from '../../../utils';
import { ConfigContext } from './App';
import FeaturedProductCard from './FeaturedProductCard';

const FeaturedProducts = () => {
  const { ims } = useContext(ContainerContext);
  const accessToken = useMemo(() => ims?.getAccessToken()?.token, [ims]);
  const { ROUTES } = useContext(ConfigContext) ?? {};
  const [products, setProducts] = useState([null, null, null]);
  const [encounteredError, setEncounteredError] = useState(false);
  const [loading, setLoading] = useState(false);
  const browseLink = useRootHref(ROUTES.BROWSE ?? '');

  useEffect(() => {
    let mounted = true;
    const handler = handleApiCall(featuredApi.featuredProducts, { authToken: accessToken });
    (async () => {
      try {
        setLoading(true);
        setEncounteredError(false);
        const response = await handler.run();
        if (mounted) {
          setProducts(response);
        }
      } catch (err) {
        if (err.code !== 20) {
          error(err.userMessage, { timeout: 0 });
          console.error(err);
          if (mounted) {
            setEncounteredError(true);
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
      handler.controller.abort();
    };
  }, [accessToken]);

  return (
    <Flex gap="size-200" marginY="size-500" wrap="wrap" data-testid="featured-products">
      <ErrorBoundary isError={encounteredError || !products} errorMessage="Failed to fetch featured products.">
        {() => (
          <>
            {products?.map((product, index) => (
              <FeaturedProductCard
                key={product ? product.name : index}
                title={product?.label}
                subtitle={`Explore over ${product?.count} apps`}
                imageLink={product?.featuredImage}
                link={`${browseLink}?product=${product?.key}&sort=RELEVANCE`}
                loading={loading}
                dataTestID={`featured-product-${index + 1}`}
              />
            ))}
          </>
        )}
      </ErrorBoundary>
    </Flex>
  );
};

export default FeaturedProducts;
